import { type JSX, type ReactNode, useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Schliesskreuz } from './Schliesskreuz';

/**
 * Ein Fenster über der Maske — für das Bearbeiten einer Tabellenzeile.
 *
 * ============================================================================
 * WARUM NICHT UNTER DER TABELLE
 * ============================================================================
 *
 * Bis hierher öffnete ein „Bearbeiten" das Formular UNTER der Liste. Das ist
 * der bequemste Bau und im Betrieb der unbequemste Ort:
 *
 *   * Bei zwanzig Zeilen steht das Formular unterhalb des Bildschirmrands.
 *     Wer die achtzehnte Zeile anfasst, sieht nach dem Klick — nichts. Die
 *     Maske hat reagiert, nur nicht sichtbar.
 *   * Die Zeile, um die es geht, ist beim Ausfüllen nicht mehr im Blick. Wer
 *     prüfen will, ob er die richtige erwischt hat, muss zurückrollen.
 *   * Zwischen Liste und Formular liegt kein Rand. Ob eine Eingabe schon gilt
 *     oder noch im Entwurf steht, sagt nur die Beschriftung des Knopfes.
 *
 * Das Fenster nimmt alle drei weg: Es steht dort, wo der Blick ohnehin ist,
 * es hat einen sichtbaren Rand, und es lässt sich nur über einen benannten
 * Weg wieder verlassen.
 *
 * ============================================================================
 * DIE ESC-TASTE SCHLIESST, DER SCHLEIER NICHT
 * ============================================================================
 *
 * Das ist der einzige Punkt, an dem dieses Fenster vom Suchfenster (M-07)
 * abweicht, und der Grund ist der Inhalt: Dort steht eine Suche, hier eine
 * halb ausgefüllte Eingabe. Esc ist ein Tastendruck, den man macht, weil man
 * gehen will; ein Klick neben das Fenster ist häufig ein Fehlgriff — und der
 * darf keine Arbeit kosten.
 *
 * Der Schleier bleibt trotzdem: Er dunkelt die Maske ab, damit sichtbar ist,
 * wohin Esc zurückführt, und er fängt Klicks ab, die sonst in der Liste
 * dahinter etwas anfassen würden.
 *
 * ============================================================================
 * DER FOKUS BLEIBT DRIN UND KOMMT ZURÜCK (NFA-14, WCAG 2.4.3, 2.1.2)
 * ============================================================================
 *
 * Ein Fenster, aus dem die Tabulatortaste hinausführt, ist für ein
 * Vorleseprogramm kein Fenster, sondern eine Zwischenüberschrift: Der Leser
 * wandert weiter in eine Liste, die er nicht mehr bedienen kann, und findet
 * nicht zurück. Deshalb drei Dinge:
 *
 *   1. Beim Öffnen springt der Fokus auf das erste Bedienelement.
 *   2. Tabulator läuft im Kreis; er verlässt das Fenster nicht.
 *   3. Beim Schließen geht er dorthin zurück, wo er herkam — auf den
 *      „Bearbeiten"-Knopf der Zeile, aus der man kam.
 *
 * ============================================================================
 * DAS FENSTER HÄNGT AM BODY (Auftrag CRM-187)
 * ============================================================================
 *
 * Befund vom 10.10.2026 am iPad quer: In den Postfächern lag über der
 * Vorgangsansicht ein leeres weißes Feld mit Trennlinien, die Ansicht selbst
 * grau. Es war der Dialog „Folgeaktion": Er stand im DOM in der Vorschau der
 * Liste, und die klebt und rollt in sich (CRM-55). Safari auf dem iPad
 * schneidet `position: fixed` in einem solchen Rollbehälter auf dessen Kasten
 * zu — vom Schleier blieb das Grau über der Vorschau, vom Fenster der rechte
 * Rand mit den Linien zwischen den Zeilen, ohne Text. Auch ohne den Schnitt
 * lag das Fenster in der Stapelebene der klebenden Vorschau und damit unter
 * dem festen Kopf (Chromium zeigt es so).
 *
 * Das Fenster wird deshalb per Portal an `document.body` gehängt, gleich, wo
 * es im Baum steht. React reicht Ereignisse weiter den Baum hinauf wie zuvor;
 * nur Stilregeln über einen Vorfahren (`.liste__vorschau .feldraster`)
 * greifen im Fenster nicht mehr — gewollt, es ist kein Teil der Vorschau.
 */

/** Was den Tabulator aufnimmt. `[tabindex="-1"]` ausdrücklich nicht. */
const BEDIENBAR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function Dialog({
  titel,
  unterzeile,
  breite = 'mittel',
  beiSchliessen,
  children,
}: {
  readonly titel: string;
  /** Ein Satz neben dem Titel — wozu das Fenster da ist, nicht was es heißt. */
  readonly unterzeile?: string | undefined;
  /** `breit` für Formulare mit Textflächen, `schmal` für ein paar Felder. */
  readonly breite?: 'schmal' | 'mittel' | 'breit';
  readonly beiSchliessen: () => void;
  readonly children: ReactNode;
}): JSX.Element {
  const fenster = useRef<HTMLDivElement>(null);
  const titelId = useId();

  /*
   * WOHIN ESC ZURÜCKFÜHRT.
   *
   * Das Element, das den Fokus hatte, als das Fenster aufging. Ohne dieses
   * Merken landet der Fokus am Seitenanfang, und wer mit der Tastatur
   * arbeitet, tabbt sich durch die halbe Navigation zurück zu der Zeile, die
   * er gerade bearbeitet hat.
   */
  const herkunft = useRef<HTMLElement | null>(null);

  useEffect(() => {
    herkunft.current = document.activeElement as HTMLElement | null;
    /*
     * IN DEN INHALT UND NICHT AUF DAS KREUZ.
     *
     * Das Schließkreuz steht in der Titelzeile und damit im Quelltext VOR dem
     * Formular. Wer den Fokus einfach auf das erste bedienbare Element setzt,
     * landet auf dem Abbruch — und ein Fenster, das mit „Abbrechen" im Fokus
     * aufgeht, lädt zum Wegdrücken ein. Gesucht wird deshalb im Inhalt; erst
     * wenn dort nichts zu bedienen ist, nimmt das Fenster den Fokus selbst.
     */
    const inhalt = fenster.current?.querySelector<HTMLElement>('.dialog__inhalt');
    /*
     * Die Rückfrage (Auftrag CRM-103) kehrt das um: Dort ist der Abbruch der
     * sichere Weg, und wer Enter drückt, soll nichts auslösen. Sie markiert
     * ihn mit `data-erstfokus`.
     */
    const ziel =
      inhalt?.querySelector<HTMLElement>('[data-erstfokus]') ??
      inhalt?.querySelector<HTMLElement>(BEDIENBAR) ??
      null;
    if (ziel !== null) ziel.focus();
    else fenster.current?.focus();

    return () => {
      herkunft.current?.focus();
    };
  }, []);

  /*
   * DIE MASKE DAHINTER ROLLT NICHT MIT.
   *
   * Ohne diese Sperre scrollt das Mausrad über dem Schleier die Liste
   * darunter — und wer das Fenster schließt, steht an einer anderen Stelle
   * der Tabelle als der, aus der er kam.
   */
  useEffect(() => {
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = vorher;
    };
  }, []);

  const beiTaste = useCallback(
    (ereignis: React.KeyboardEvent<HTMLDivElement>): void => {
      if (ereignis.key !== 'Escape' && ereignis.key !== 'Tab') return;
      /*
       * EIN FENSTER IM FENSTER (Auftrag CRM-103): Die Rückfrage öffnet sich
       * auch aus einem Dialog heraus. Ohne den Halt hier schlösse Esc beide,
       * und der Tabulatorkreis des äußeren griffe in den des inneren.
       */
      ereignis.stopPropagation();
      if (ereignis.key === 'Escape') {
        ereignis.preventDefault();
        beiSchliessen();
        return;
      }

      /*
       * DER KREIS (WCAG 2.1.2).
       *
       * Gezählt wird bei jedem Druck neu und nicht einmal beim Öffnen: Die
       * Formulare hier blenden Felder ein und aus — die Platzzahl erscheint
       * mit der Belegungsart, eine Fassung mit ihrer Art. Eine beim Öffnen
       * eingefrorene Liste führte den Fokus danach auf Elemente, die es nicht
       * mehr gibt.
       */
      const bedienbar = [...(fenster.current?.querySelectorAll<HTMLElement>(BEDIENBAR) ?? [])];
      if (bedienbar.length === 0) return;
      const erstes = bedienbar[0]!;
      const letztes = bedienbar[bedienbar.length - 1]!;
      const aktiv = document.activeElement;

      if (ereignis.shiftKey && (aktiv === erstes || aktiv === fenster.current)) {
        ereignis.preventDefault();
        letztes.focus();
      } else if (!ereignis.shiftKey && aktiv === letztes) {
        ereignis.preventDefault();
        erstes.focus();
      }
    },
    [beiSchliessen],
  );

  return createPortal(
    <div className="dialog" onKeyDown={beiTaste}>
      {/*
        DER SCHLEIER IST KEIN KNOPF.
        Er schließt nicht — anders als beim Suchfenster, wo nichts zu
        verlieren ist. Hier steht eine halb ausgefüllte Eingabe dahinter, und
        ein Fehlgriff neben das Fenster darf sie nicht kosten. Er ist deshalb
        auch für ein Vorleseprogramm nichts: `aria-hidden`.
      */}
      <div className="dialog__schleier" aria-hidden="true" />

      <div
        className={`dialog__fenster dialog__fenster--${breite}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        ref={fenster}
        tabIndex={-1}
      >
        <div className="dialog__titel">
          <h2 id={titelId}>{titel}</h2>
          {unterzeile !== undefined && <span className="dialog__hinweis">{unterzeile}</span>}
          {/*
            Das Kreuz steht ZULETZT in der Reihenfolge des Tabulators innerhalb
            der Titelzeile, aber vor dem Inhalt — wer mit der Tastatur kommt,
            landet im ersten Feld und nicht auf dem Abbruch.
          */}
          <Schliesskreuz beschriftung={`${titel} schließen`} beiKlick={beiSchliessen} />
        </div>

        <div className="dialog__inhalt">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
